import { useForm, useFieldArray, useFormContext } from "react-hook-form";
import { Form } from "../../components/shadcn-ui/form";
import { Card } from "../../components/shadcn-ui/card";
import { Button } from "../../components/shadcn-ui/button";
import { Input } from "../../components/shadcn-ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/shadcn-ui/select";
import { useToast } from "@customHooks/useToast";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { createGroup } from "../../features/groups/services/groupsService";
import { INDIAN_CITIES, CITY_MAP } from "features/loanApplications/formFieldsConfig/indiaStatesList";
import { createLead, checkLeadExists } from "../../features/leads/services/leadsService";
import { Separator } from "@components/shadcn-ui/separator";
import { Plus, Trash2, UserCheck } from "lucide-react";
import { LEADS_FORM_FIELDS } from "features/loanApplications/formFieldsConfig";
import { DynamicFormField, FieldType } from "../../components/ui/DynamicInput";
import { isFieldAsCity } from "@lib/utils";
import { useAuth } from "context/AuthContext";

type MemberForm = {
    firstName: string;
    lastName: string;
    phoneNumber: string;
    email: string;
    addressLineOne: string;
    addressLineTwo: string;
    city: string;
    state: string;
    postalCode: string;
    referrer: string;
    channel: string;
    pilId?: string;
    isCoordinator: boolean;
    leadExists: boolean;
    contactId?: string;
    originalData?: any; // Store original lead data to check for existing values
};

type FormData = {
    name: string;
    city: string;
    bdManagerName: string;
    bdManagerPhoneNumber: string;
    members: MemberForm[];
};

const MemberRow = ({
    index,
    onRemove,
    onPhoneBlur,
    toggleCoordinator,
    fieldsLength
}: {
    index: number;
    onRemove: (index: number) => void;
    onPhoneBlur: (index: number, val: string) => void;
    toggleCoordinator: (index: number) => void;
    fieldsLength: number;
}) => {
    const form = useFormContext<FormData>();
    const member = form.watch(`members.${index}`);

    if (!member) return null;

    const onPhoneChange = (index: number, val: string) => {
        const currentMember = form.getValues(`members.${index}`);
        // Only reset if we had a lead found and the number changes (e.g. user deletes a digit)
        if (currentMember.leadExists && val !== currentMember.phoneNumber) {
            form.setValue(`members.${index}.leadExists`, false);
            form.setValue(`members.${index}.contactId`, undefined);
            form.setValue(`members.${index}.originalData`, undefined);
            // Clear fields or let them be overwritten? 
            // Ideally we just 'unlock' them. `isDisabled` logic depends on `leadExists` + `hasExistingValue`.
            // By setting `leadExists` to false, they become editable.
        }
        form.setValue(`members.${index}.phoneNumber`, val);
    };

    return (
        <Card className="p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                    <h4 className="font-medium">Member {index + 1}</h4>
                    {member.isCoordinator && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded flex items-center gap-1">
                            <UserCheck className="h-3 w-3" />
                            Coordinator
                        </span>
                    )}
                    {member.leadExists && (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            ✓ Existing Lead
                        </span>
                    )}
                </div>
                <div className="flex gap-2">
                    {!member.isCoordinator && (
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => toggleCoordinator(index)}
                        >
                            Make Coordinator
                        </Button>
                    )}
                    {fieldsLength > 1 && (
                        <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => onRemove(index)}
                        >
                            <Trash2 className="h-4 w-4" />
                        </Button>
                    )}
                </div>
            </div>

            {/* All Lead Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {LEADS_FORM_FIELDS.map((leadField: FieldType, fieldIndex: number) => {
                    const fieldName = leadField.name === "city" ? "city" : leadField.name;
                    const fullFieldName = `members.${index}.${fieldName}` as any;

                    // Watch state for THIS specific member
                    const memberState = form.watch(`members.${index}.state`);

                    const extraProps =
                        leadField.name === "phoneNumber"
                            ? {
                                onBlur: (e: React.FocusEvent<HTMLInputElement>) => onPhoneBlur(index, e.target.value)
                            }
                            : {};

                    // Custom handleChange for phone number
                    const handleChange = leadField.name === "phoneNumber"
                        ? (_: string, value: any) => onPhoneChange(index, value)
                        : leadField.name === "state"
                            ? (name: string, value: any) => {
                                form.setValue(name as any, value);
                                form.setValue(`members.${index}.city`, "");
                            }
                            : undefined;

                    // Check if the field has a value in the existing lead data
                    const hasExistingValue = () => {
                        if (!member.originalData) return false;
                        if (leadField.name === "phoneNumber" || leadField.name === "channel") return false;

                        if (leadField.name === "addressLineOne" || leadField.name === "addressLineTwo" || leadField.name === "city" || leadField.name === "state" || leadField.name === "postalCode") {
                            return !!member.originalData.address?.[leadField.name];
                        }
                        return !!member.originalData[leadField.name];
                    };

                    const isFieldDisabled =
                        (member.leadExists &&
                            leadField.name !== "phoneNumber" &&
                            leadField.name !== "channel" &&
                            hasExistingValue()) ||
                        leadField.name === "referrer" ||
                        (isFieldAsCity(leadField.name) && !memberState && !member.leadExists);

                    const dynamicOptions =
                        isFieldAsCity(leadField.name)
                            ? CITY_MAP[memberState] || []
                            : leadField.options || [];

                    return (
                        <div key={fieldIndex} className="w-full">
                            <DynamicFormField
                                field={{ ...leadField, name: fullFieldName, options: dynamicOptions } as FieldType}
                                isDisabled={isFieldDisabled}
                                extraProps={extraProps}
                                handleChange={handleChange}
                                {...form.register(fullFieldName)}
                            />
                            {form.formState.errors.members?.[index]?.[fieldName as keyof MemberForm] && (
                                <p className="text-red-500 text-xs mt-1">
                                    {(form.formState.errors.members?.[index]?.[fieldName as keyof MemberForm] as any)?.message}
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

export function CreateGroupPage() {
    const navigate = useNavigate();
    const { toast } = useToast();
    const { userDetails } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const form = useForm<FormData>({
        mode: "all",
        defaultValues: {
            name: "",
            city: "",
            bdManagerName: "",
            bdManagerPhoneNumber: "",
            members: [
                {
                    firstName: "",
                    lastName: "",
                    phoneNumber: "",
                    email: "",
                    addressLineOne: "",
                    addressLineTwo: "",
                    city: "",
                    state: "",
                    postalCode: "",
                    referrer: userDetails?.email || "",
                    channel: "",
                    pilId: "",
                    isCoordinator: true,
                    leadExists: false,
                },
            ],
        },
    });

    const { fields, append, remove } = useFieldArray({
        control: form.control,
        name: "members",
    });

    const onPhoneBlur = async (index: number, phoneNumber: string) => {
        if (phoneNumber && phoneNumber.length === 10) {
            try {
                const lead = await checkLeadExists(phoneNumber) as any;
                if (lead) {
                    // Check if lead is already a member of a group
                    if (lead.metaData?.memberOf) {
                        toast({
                            variant: "destructive",
                            title: "Lead already in a group",
                            description: `${lead.firstName} is already a member of "${lead.metaData.memberOf.name}"`,
                        });

                        // Clear the phone number field to force user to change it
                        form.setValue(`members.${index}.phoneNumber`, "");
                        return;
                    }

                    form.setValue(`members.${index}.firstName`, lead.firstName || "");
                    form.setValue(`members.${index}.lastName`, lead.lastName || "");
                    form.setValue(`members.${index}.email`, lead.email || "");

                    // Map address fields
                    if (lead.address) {
                        form.setValue(`members.${index}.addressLineOne`, lead.address.addressLineOne || "");
                        form.setValue(`members.${index}.addressLineTwo`, lead.address.addressLineTwo || "");
                        form.setValue(`members.${index}.state`, lead.address.state || "");
                        // Defer setting city to ensure state-dependent options are updated
                        const cityValue = lead.address.city;
                        if (cityValue) {
                            setTimeout(() => {
                                form.setValue(`members.${index}.city`, cityValue);
                            }, 0);
                        }
                        form.setValue(`members.${index}.postalCode`, lead.address.postalCode || "");
                    }

                    if (lead.source) {
                        form.setValue(`members.${index}.channel`, lead.source.channel || "");
                    }

                    form.setValue(`members.${index}.leadExists`, true);
                    form.setValue(`members.${index}.contactId`, lead.id);
                    form.setValue(`members.${index}.originalData`, lead);
                    toast({
                        description: `Lead found for ${phoneNumber}!`,
                    });
                } else {
                    form.setValue(`members.${index}.leadExists`, false);
                    form.setValue(`members.${index}.contactId`, undefined);
                }
            } catch (error) {
                console.error("Error checking lead:", error);
            }
        }
    };

    const toggleCoordinator = (index: number) => {
        // Set all to false first
        fields.forEach((_, i) => {
            form.setValue(`members.${i}.isCoordinator`, false);
        });
        // Set selected one to true
        form.setValue(`members.${index}.isCoordinator`, true);
    };

    const onSubmit = async (formState: FormData) => {
        // Validate at least one coordinator
        const hasCoordinator = formState.members.some(m => m.isCoordinator);
        if (!hasCoordinator) {
            toast({
                variant: "destructive",
                description: "Please select at least one coordinator",
            });
            return;
        }

        setIsSubmitting(true);
        try {
            // Process all members - create leads if needed and get contactIds
            const processedMembers = await Promise.all(
                formState.members.map(async (member) => {
                    let contactId = member.contactId;

                    // Create lead if doesn't exist
                    if (!member.leadExists) {
                        const leadData = {
                            firstName: member.firstName,
                            lastName: member.lastName,
                            phoneNumber: member.phoneNumber,
                            email: member.email,
                            address: {
                                addressLineOne: member.addressLineOne,
                                addressLineTwo: member.addressLineTwo,
                                city: member.city,
                                state: member.state,
                                postalCode: member.postalCode,
                            },
                            referral: member.referrer,
                            channel: member.channel,
                            pilId: member.pilId,
                        };

                        const createdLead = await createLead(leadData) as any;
                        contactId = createdLead.id;
                    }

                    return {
                        contactId: contactId!,
                        lastName: member.lastName,
                        phoneNumber: member.phoneNumber,
                        role: member.isCoordinator ? ("COORDINATOR" as const) : ("MEMBER" as const),
                    };
                })
            );

            // Create group with all members
            const coordinator = processedMembers.find(m => m.role === "COORDINATOR");
            if (!coordinator) {
                throw new Error("No coordinator found in members");
            }

            await createGroup({
                name: formState.name,
                city: formState.city,
                coordinator: coordinator as { contactId: string; lastName: string; phoneNumber: string; role: "COORDINATOR" },
                members: processedMembers,
                bdManager: {
                    name: formState.bdManagerName,
                    phoneNumber: formState.bdManagerPhoneNumber,
                },
                metaData: {
                    type: "loan_group",
                    desc: ""
                }
            });

            toast({
                title: "Group created successfully!",
                description: `Group "${formState.name}" with ${formState.members.length} member(s)`,
            });
            navigate("/dashboard/groups");
        } catch (err: any) {
            console.error("Error creating group:", err);
            toast({
                variant: "destructive",
                description: err?.message || "Failed to create group.",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <strong className="block mt-0 mb-2 text-xl bg-blue-200 p-2 pl-4 rounded-sm">
                Create Group
            </strong>
            <Card className="p-4 h-full md:pt-6 overflow-auto">
                <div className="flex flex-col md:pl-4 flex-wrap">
                    <div className="w-full">
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)}>
                                {/* Group Details Section */}
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold mb-4">Group Information</h3>
                                    <div className="flex flex-col gap-y-4 max-w-2xl">
                                        <div>
                                            <label className="text-sm font-medium">Group Name *</label>
                                            <Input
                                                placeholder="Enter group name"
                                                {...form.register("name", {
                                                    required: "Group name is required",
                                                    minLength: { value: 3, message: "Min 3 characters" },
                                                })}
                                            />
                                            {form.formState.errors.name && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.name.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium">Group City *</label>
                                            <Select
                                                onValueChange={(value) => form.setValue("city", value)}
                                                value={form.watch("city")}
                                            >
                                                <SelectTrigger>
                                                    <SelectValue placeholder="Select a city" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {INDIAN_CITIES.map((city) => (
                                                        <SelectItem key={city} value={city}>
                                                            {city}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            {form.formState.errors.city && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.city.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium">BD Manager Name *</label>
                                            <Input
                                                placeholder="Enter BD Manager name"
                                                {...form.register("bdManagerName", { required: "Required" })}
                                            />
                                            {form.formState.errors.bdManagerName && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.bdManagerName.message}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="text-sm font-medium">BD Manager Phone Number *</label>
                                            <Input
                                                placeholder="10-digit phone number"
                                                maxLength={10}
                                                {...form.register("bdManagerPhoneNumber", {
                                                    required: "Required",
                                                    pattern: { value: /^\d{10}$/, message: "Must be 10 digits" },
                                                })}
                                            />
                                            {form.formState.errors.bdManagerPhoneNumber && (
                                                <p className="text-red-500 text-xs mt-1">{form.formState.errors.bdManagerPhoneNumber.message}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <Separator className="my-6" />

                                {/* Members Section */}
                                <div className="mb-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h3 className="text-lg font-semibold">Group Members</h3>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                                append({
                                                    firstName: "",
                                                    lastName: "",
                                                    phoneNumber: "",
                                                    email: "",
                                                    addressLineOne: "",
                                                    addressLineTwo: "",
                                                    city: "",
                                                    state: "",
                                                    postalCode: "",
                                                    referrer: userDetails?.email || "",
                                                    channel: "",
                                                    pilId: "",
                                                    isCoordinator: false,
                                                    leadExists: false,
                                                })
                                            }
                                        >
                                            <Plus className="h-4 w-4 mr-1" />
                                            Add Member
                                        </Button>
                                    </div>

                                    {fields.map((field, index) => (
                                        <MemberRow
                                            key={field.id}
                                            index={index}
                                            onRemove={remove}
                                            onPhoneBlur={onPhoneBlur}
                                            toggleCoordinator={toggleCoordinator}
                                            fieldsLength={fields.length}
                                        />
                                    ))}
                                </div>

                                <div className="flex gap-2 mt-6">
                                    <Button type="submit" className="btn-primary" disabled={isSubmitting}>
                                        {isSubmitting ? "Creating..." : "Create Group"}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => navigate("/dashboard/groups")}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </div>
                </div>
            </Card>
        </>
    );
}
